import { useState, useEffect } from 'react';
import { 
  Typography, 
  Card,
  Space,
  Button, 
  Table, 
  Input,
  Tag,
  message,
  Spin,
  Tooltip,
  Popconfirm,
  Modal
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  PlusOutlined, 
  EditOutlined, 
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
  PercentageOutlined 
} from '@ant-design/icons';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../types';
import { formatPrice } from '../../utils/format';
import TierPricingModal from '../../components/TierPricingModal';

const { Title } = Typography;

interface AdminProductsProps {
  companyView?: boolean;
}

export default function AdminProducts({ companyView }: AdminProductsProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productTiers, setProductTiers] = useState<{[key: string]: number}>({});
  const navigate = useNavigate();

  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchProductTiers();
  }, []);

  async function fetchProductTiers() {
    try {
      const { data, error } = await supabase
        .from('product_price_tiers')
        .select('product_id, id');
      
      if (error) throw error;
      
      // Count tiers per product
      const tierCounts: {[key: string]: number} = {};
      data?.forEach(tier => {
        if (tier.product_id) {
          tierCounts[tier.product_id] = (tierCounts[tier.product_id] || 0) + 1;
        }
      });
      
      setProductTiers(tierCounts);
    } catch (error) {
      console.error('Error fetching product tiers:', error);
    }
  }

  async function fetchProducts() {
    setLoading(true);
    try {
      console.log('Fetching all products...');
      const { data, error } = await supabase
        .from('producten')
        .select('*')
        .order('bijgewerkt_op', { ascending: false });
      
      if (error) {
        console.error('Error fetching products:', error);
        throw error;
      }
      
      console.log('Products fetched:', data?.length || 0);
      setProducts(data || []);
    } catch (error) {
      console.error('Error in fetchProducts:', error);
      message.error('Fout bij het ophalen van producten');
    } finally {
      setLoading(false);
    }
  }

  const handleOpenTierModal = (product: Product) => {
    setSelectedProduct(product);
    setTierModalOpen(true);
  };

  const handleTierModalClose = () => {
    setTierModalOpen(false);
    setSelectedProduct(null);
    // Refresh tier counts after closing the modal
    fetchProductTiers();
  };

  const filteredProducts = products.filter(product => 
    product.naam.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.artikelnummer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeleteProduct = async (productId: string) => {
    Modal.confirm({
      title: 'Delete Product',
      content: 'Are you sure you want to delete this product? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLoading(true);
          
          // First delete all related records (materials, images, certificates)
          await supabase
            .from('product_materialen')
            .delete()
            .eq('product_id', productId);
            
          await supabase
            .from('product_afbeeldingen')
            .delete()
            .eq('product_id', productId);
            
          await supabase
            .from('product_certificeringen')
            .delete()
            .eq('product_id', productId);
            
          await supabase
            .from('product_access')
            .delete()
            .eq('product_id', productId);
          
          // Delete tier pricing if it exists
          await supabase
            .from('product_price_tiers')
            .delete()
            .eq('product_id', productId);
            
          // Finally delete the product itself
          const { error } = await supabase
            .from('producten')
            .delete()
            .eq('id', productId);
            
          if (error) throw error;
          
          message.success('Product deleted successfully');
          fetchProducts(); // Refresh the product list
          fetchProductTiers(); // Refresh tier counts
        } catch (error: any) {
          console.error('Error deleting product:', error);
          message.error('Error deleting product: ' + error.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns: ColumnsType<Product> = [
    {
      title: 'Artikelnummer',
      dataIndex: 'artikelnummer',
      key: 'artikelnummer',
      sorter: (a: Product, b: Product) => a.artikelnummer.localeCompare(b.artikelnummer),
    },
    {
      title: 'Naam',
      dataIndex: 'naam',
      key: 'naam',
      sorter: (a: Product, b: Product) => a.naam.localeCompare(b.naam),
      render: (text: string) => (
        <Tooltip title={text}>
          <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Prijs',
      dataIndex: 'prijs',
      key: 'prijs',
      sorter: (a: Product, b: Product) => a.prijs - b.prijs,
      render: (price: number, record: Product) => (
        <div>
          {formatPrice(price)}
          {productTiers[record.id] && (
            <Tooltip title={`${productTiers[record.id]} staffelprijzen ingesteld`}>
              <Tag color="blue" style={{ marginLeft: 8 }}>
                Staffel
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Laatst Bijgewerkt',
      dataIndex: 'bijgewerkt_op',
      key: 'bijgewerkt_op',
      render: (date: string) => new Date(date).toLocaleDateString('nl-NL'),
      sorter: (a: Product, b: Product) => new Date(a.bijgewerkt_op).getTime() - new Date(b.bijgewerkt_op).getTime(),
      responsive: ['md'],
    },
    {
      title: 'Acties',
      key: 'actions',
      render: (_: unknown, record: Product) => (
        <Space>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => navigate(`/admin/products/${record.id}/view`)}
            size={isMobile ? 'small' : 'middle'}
          />
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => navigate(`/admin/products/${record.id}`)}
            size={isMobile ? 'small' : 'middle'}
          />
          <Button 
            type="link"
            icon={<PercentageOutlined />}
            onClick={() => handleOpenTierModal(record)}
            size={isMobile ? 'small' : 'middle'}
            title="Staffelprijzen beheren"
          />
          <Button 
            type="link"
            danger
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteProduct(record.id)}
            size={isMobile ? 'small' : 'middle'}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        style={{
          borderRadius: '8px',
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '16px' : '0'
          }}>
            <Title level={4} style={{ margin: 0 }}>Productenbeheer</Title>
            <Space 
              size="middle" 
              wrap={isMobile}
              style={{ 
                width: isMobile ? '100%' : 'auto',
                justifyContent: isMobile ? 'space-between' : 'flex-end',
                display: 'flex' 
              }}
            >
              <Input
                placeholder="Zoek producten..."
                prefix={<SearchOutlined />}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: isMobile ? '60%' : 200 }}
              />
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => navigate('/admin/products/new')}
              >
                Nieuw Product
              </Button>
            </Space>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Table 
              columns={columns} 
              dataSource={filteredProducts} 
              rowKey="id"
              pagination={{ 
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50'],
                showTotal: (total, range) => `${range[0]}-${range[1]} van ${total} producten`,
              }}
            />
          )}
        </Space>
      </Card>

      {selectedProduct && (
        <TierPricingModal
          productId={selectedProduct.id}
          productName={selectedProduct.naam}
          basePrice={selectedProduct.prijs}
          open={tierModalOpen}
          onClose={handleTierModalClose}
        />
      )}
    </div>
  );
} 